import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';
import { validators, validateForm, validatePasswordMatch } from '../utils/validation';
import { toast } from '../components/ui/sonner';
import { Briefcase, Mail, Lock, User, Phone, Building2, Users, ArrowRight, Eye, EyeOff, CheckCircle, ShieldQuestion, Camera } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'worker',
    security_question: '',
    security_answer: '',
    profile_image: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [touched, setTouched] = useState({});

  const step1Schema = {
    first_name: [validators.required, validators.name],
    last_name: [validators.required, validators.name],
    email: [validators.required, validators.email],
    phone: [validators.phone],
    password: [validators.required, validators.strongPassword],
    confirmPassword: [validators.required, (v, vals) => validatePasswordMatch(vals.password, v)],
  };
  const { errors: fieldErrors, isValid: step1Valid } = validateForm(formData, step1Schema);
  const markTouched = (field) => setTouched((t) => ({ ...t, [field]: true }));
  const fieldError = (field) => (touched[field] && fieldErrors[field] ? fieldErrors[field] : null);

  const securityQuestions = [
    "What is your mother's maiden name?",
    "What was the name of your first pet?",
    "What city were you born in?",
    "What was the name of your elementary school?",
    "What is your favorite movie?",
    "What was your childhood nickname?",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (step === 1) {
      setTouched({ first_name: true, last_name: true, email: true, phone: true, password: true, confirmPassword: true });
      if (!step1Valid) {
        toast.error(Object.values(fieldErrors)[0]);
        return;
      }
      setStep(2);
      return;
    }

    setLoading(true);
    try {
      const response = await apiCall('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          full_name: `${formData.first_name} ${formData.last_name}`.trim(),
          phone: formData.phone,
          role: formData.role,
          security_question: formData.security_question || null,
          security_answer: formData.security_answer || null,
          profile_image: formData.profile_image || null,
        }),
      });

      // Email verification gate: send the user to /verify-email which will
      // auto-send a single OTP on mount (idempotent on the backend).
      toast.success('Account created! Verify your email to sign in.');
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipSecurity = async () => {
    setLoading(true);
    try {
      const response = await apiCall('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          full_name: `${formData.first_name} ${formData.last_name}`.trim(),
          phone: formData.phone,
          role: formData.role,
        }),
      });

      toast.success('Account created! Verify your email to sign in.');
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { 
      value: 'worker', 
      label: 'Worker', 
      description: 'Find jobs and get paid instantly',
      icon: <User className="w-5 h-5" />
    },
    { 
      value: 'employer', 
      label: 'Employer', 
      description: 'Post jobs and hire verified professionals',
      icon: <Building2 className="w-5 h-5" />
    },
    { 
      value: 'agent', 
      label: 'Staffing Agent', 
      description: 'Manage workers and client relationships',
      icon: <Users className="w-5 h-5" />
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          {/* Logo */}
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-10 h-10 rounded-xl bg-cyan-400 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-slate-950" />
            </div>
            <span className="text-xl font-bold text-white font-['Manrope']">
              Scensob<span className="text-cyan-400">Connect</span>
            </span>
          </div>
          
          {/* Form Header */}
          <div className="mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2 font-['Manrope']">
              {step === 1 ? 'Create your account' : 'Security Setup'}
            </h2>
            <p className="text-slate-500">
              {step === 1 ? (
                <>
                  Already have an account?{' '}
                  <button 
                    onClick={() => navigate('/login')} 
                    className="text-cyan-400 font-semibold hover:text-cyan-300 transition-colors"
                  >
                    Sign in
                  </button>
                </>
              ) : (
                'Step 2 of 2 - Add security for account recovery'
              )}
            </p>
          </div>
          
          {/* Register Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {step === 1 ? (
              <>
            {/* Role Selection */}
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-3">I want to...</label>
              <div className="grid grid-cols-1 gap-3">
                {roleOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`relative flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      formData.role === option.value
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-slate-800 hover:border-slate-700 bg-slate-900/50'
                    }`}
                    data-testid={`register-role-${option.value}`}
                  >
                    <input
                      type="radio"
                      name="role"
                      value={option.value}
                      checked={formData.role === option.value}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="sr-only"
                    />
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                      formData.role === option.value
                        ? 'bg-cyan-400 text-slate-950'
                        : 'bg-slate-800 text-slate-400'
                    }`}>
                      {option.icon}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-white">{option.label}</div>
                      <div className="text-sm text-slate-500">{option.description}</div>
                    </div>
                    {formData.role === option.value && (
                      <CheckCircle className="w-5 h-5 text-cyan-400" />
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">First Name</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  onBlur={() => markTouched('first_name')}
                  className={`w-full h-12 px-4 bg-slate-950 border rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all ${fieldError('first_name') ? 'border-red-500' : 'border-slate-800'}`}
                  placeholder="John"
                  required
                  data-testid="register-firstname-input"
                />
                {fieldError('first_name') && <p className="text-xs text-red-400 mt-1.5" data-testid="register-firstname-error">{fieldError('first_name')}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Last Name</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  onBlur={() => markTouched('last_name')}
                  className={`w-full h-12 px-4 bg-slate-950 border rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all ${fieldError('last_name') ? 'border-red-500' : 'border-slate-800'}`}
                  placeholder="Doe"
                  required
                  data-testid="register-lastname-input"
                />
                {fieldError('last_name') && <p className="text-xs text-red-400 mt-1.5" data-testid="register-lastname-error">{fieldError('last_name')}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Email address</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="w-5 h-5 text-slate-600" />
                </div>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  onBlur={() => markTouched('email')}
                  className={`w-full h-12 pl-12 pr-4 bg-slate-950 border rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all ${fieldError('email') ? 'border-red-500' : 'border-slate-800'}`}
                  placeholder="you@company.com"
                  required
                  data-testid="register-email-input"
                />
              </div>
              {fieldError('email') && <p className="text-xs text-red-400 mt-1.5" data-testid="register-email-error">{fieldError('email')}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Phone Number <span className="text-slate-600">(optional)</span></label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Phone className="w-5 h-5 text-slate-600" />
                </div>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  onBlur={() => markTouched('phone')}
                  className={`w-full h-12 pl-12 pr-4 bg-slate-950 border rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all ${fieldError('phone') ? 'border-red-500' : 'border-slate-800'}`}
                  placeholder="+1 (555) 000-0000"
                  data-testid="register-phone-input"
                />
              </div>
              {fieldError('phone') && <p className="text-xs text-red-400 mt-1.5" data-testid="register-phone-error">{fieldError('phone')}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-slate-600" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  onBlur={() => markTouched('password')}
                  className={`w-full h-12 pl-12 pr-12 bg-slate-950 border rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all ${fieldError('password') ? 'border-red-500' : 'border-slate-800'}`}
                  placeholder="Create a strong password"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5 text-slate-600 hover:text-slate-400 transition-colors" />
                  ) : (
                    <Eye className="w-5 h-5 text-slate-600 hover:text-slate-400 transition-colors" />
                  )}
                </button>
              </div>
              {fieldError('password') && <p className="text-xs text-red-400 mt-1.5" data-testid="register-password-error">{fieldError('password')}</p>}
              <p className="text-xs text-slate-600 mt-1.5">Must be 8+ characters with an uppercase letter, lowercase letter, number and special character.</p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-slate-400 mb-2">Confirm Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-slate-600" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  onBlur={() => markTouched('confirmPassword')}
                  className={`w-full h-12 pl-12 pr-4 bg-slate-950 border rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all ${fieldError('confirmPassword') ? 'border-red-500' : 'border-slate-800'}`}
                  placeholder="Confirm your password"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
              {fieldError('confirmPassword') && <p className="text-xs text-red-400 mt-1.5" data-testid="register-confirm-password-error">{fieldError('confirmPassword')}</p>}
            </div>

            {/* Terms */}
            <div className="flex items-start gap-3">
              <input 
                type="checkbox" 
                className="mt-1 w-4 h-4 rounded border-slate-700 bg-slate-900 text-cyan-500 focus:ring-cyan-500" 
                required 
              />
              <span className="text-sm text-slate-500">
                I agree to the{' '}
                <a href="#" className="text-cyan-400 hover:text-cyan-300">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-cyan-400 hover:text-cyan-300">Privacy Policy</a>
              </span>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group w-full h-12 bg-cyan-400 text-slate-950 rounded-full font-semibold hover:bg-cyan-300 shadow-[0_0_15px_rgba(34,211,238,0.3)] hover:shadow-[0_0_25px_rgba(34,211,238,0.4)] transition-all hover:scale-[1.02] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
              data-testid="register-submit-button"
            >
              Continue to Security Setup
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
              </>
            ) : (
              <>
                {/* Step 2: Security Setup */}
                <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-cyan-400/20 rounded-lg">
                      <ShieldQuestion className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Account Recovery</p>
                      <p className="text-sm text-slate-500">Set up a security question to recover your account if you forget your password.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Security Question</label>
                  <select
                    value={formData.security_question}
                    onChange={(e) => setFormData({ ...formData, security_question: e.target.value })}
                    className="w-full h-12 px-4 bg-slate-950 border border-slate-800 rounded-lg text-white focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                    data-testid="register-security-question"
                  >
                    <option value="">Select a security question</option>
                    {securityQuestions.map((q) => (
                      <option key={q} value={q}>{q}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Your Answer</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <ShieldQuestion className="w-5 h-5 text-slate-600" />
                    </div>
                    <input
                      type="text"
                      value={formData.security_answer}
                      onChange={(e) => setFormData({ ...formData, security_answer: e.target.value })}
                      className="w-full h-12 pl-12 pr-4 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                      placeholder="Enter your answer"
                      data-testid="register-security-answer"
                    />
                  </div>
                  <p className="text-xs text-slate-600 mt-2">Answer is case-insensitive and will be used to verify your identity.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-2">Profile Image URL <span className="text-slate-600">(optional)</span></label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Camera className="w-5 h-5 text-slate-600" />
                    </div>
                    <input
                      type="url"
                      value={formData.profile_image}
                      onChange={(e) => setFormData({ ...formData, profile_image: e.target.value })}
                      className="w-full h-12 pl-12 pr-4 bg-slate-950 border border-slate-800 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                      placeholder="https://example.com/your-photo.jpg"
                      data-testid="register-profile-image"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 h-12 bg-slate-800 text-white rounded-full font-semibold hover:bg-slate-700 transition-all"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 group h-12 bg-cyan-400 text-slate-950 rounded-full font-semibold hover:bg-cyan-300 shadow-[0_0_15px_rgba(34,211,238,0.3)] hover:shadow-[0_0_25px_rgba(34,211,238,0.4)] transition-all hover:scale-[1.02] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
                    data-testid="register-complete-button"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-slate-950/30 border-t-slate-950 rounded-full animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        Create Account
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleSkipSecurity}
                  disabled={loading}
                  className="w-full text-sm text-slate-500 hover:text-slate-400 transition-colors py-2"
                >
                  Skip security setup (not recommended)
                </button>
              </>
            )}
          </form>
          
          {/* Footer */}
          <p className="mt-8 text-center text-xs text-slate-600">
            By creating an account, you agree to receive updates from ScensobConnect.
          </p>
        </div>
      </div>
      
      {/* Right Side - Image & Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image */}
        <img 
          src="https://images.unsplash.com/photo-1612531385446-f7e6d131e1d0?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Healthcare professional"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-slate-950/70 to-slate-950/90" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12 lg:p-16">
          <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6 font-['Manrope']">
            Join the future of<br />
            <span className="text-cyan-400">staffing</span>
          </h1>
          
          <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-md">
            Whether you're looking for jobs or hiring professionals, ScensobConnect has you covered.
          </p>
          
          {/* Benefits */}
          <div className="space-y-4">
            {[
              'GPS-based job matching & attendance',
              'Secure escrow payments',
              'Background-verified professionals',
              'Real-time messaging & notifications'
            ].map((benefit) => (
              <div key={benefit} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-cyan-400/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-cyan-400" />
                </div>
                <span className="text-slate-300">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
